import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ButtonBase, Icon, TextField } from '@material-ui/core';
import { OutlinedSelect, OptionType } from 'components/Select';
import { setFilter, resetTabFilters } from 'services/transactions/transactionsSlice';
import { useGetBuyerTransactionSellerOptionsQuery } from 'services/api/transactions';
import { debounce } from 'utils';

const TAB_KEY_MAP: Record<string, 'all' | 'easyPay' | 'card'> = {
  '': 'all',
  all: 'all',
  transactions: 'card',
  easy_pay: 'easyPay',
};

const STATUS_OPTIONS = [
  { value: 'not_paid', label: 'Not Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
];

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 16px;
  padding: 16px 0;
`;

const TextInput = styled(TextField)`
  width: 160px;
`;

const ResetButton = styled(ButtonBase)`
  padding: 4px;
  border-radius: 4px !important;
  color: rgba(0, 0, 0, 0.54);

  &:hover {
    color: rgba(0, 0, 0, 0.87);
  }
`;

type Props = {
  currentTab: string;
};

const BuyerTransactionsFilter = ({ currentTab }: Props) => {
  const dispatch = useDispatch();
  const [resetKey, setResetKey] = useState(0);
  const reduxTabKey = TAB_KEY_MAP[currentTab] ?? 'all';
  const filters = useSelector((state: any) => state.transactions[reduxTabKey].filters);

  const { keyword, vin, status } = filters;
  const { data: sellerOptions = [] } = useGetBuyerTransactionSellerOptionsQuery(
    { ...(keyword && { keyword }), ...(vin && { vin }), ...(status?.length && { status }) }
  );

  const statusValues = STATUS_OPTIONS.filter(o => filters.status?.includes(o.value));
  const sellerValues = sellerOptions.filter((o: OptionType) => filters.seller_ids?.includes(o.value));

  const debouncedSetKeyword = useCallback(
    debounce((value: string) => {
      dispatch(setFilter({ tab: reduxTabKey, key: 'keyword', value: value || null }));
    }, 500),
    [dispatch, reduxTabKey]
  );

  const debouncedSetVin = useCallback(
    debounce((value: string) => {
      dispatch(setFilter({ tab: reduxTabKey, key: 'vin', value: value ? value.toUpperCase() : null }));
    }, 500),
    [dispatch, reduxTabKey]
  );

  const handleReset = () => {
    dispatch(resetTabFilters(reduxTabKey));
    setResetKey(k => k + 1);
  };

  return (
    <Wrapper>
      <TextInput
        key={`keyword-${resetKey}`}
        placeholder="Keyword"
        defaultValue={filters.keyword ?? ''}
        onChange={e => debouncedSetKeyword(e.target.value)}
      />
      <TextInput
        key={`vin-${resetKey}`}
        placeholder="VIN Search"
        defaultValue={filters.vin ?? ''}
        onChange={e => debouncedSetVin(e.target.value)}
      />
      <OutlinedSelect
        options={sellerOptions}
        placeholder="Seller Search"
        isMulti
        withSearch
        value={sellerValues as any}
        onChange={o =>
          dispatch(
            setFilter({
              tab: reduxTabKey,
              key: 'seller_ids',
              value: Array.isArray(o) && o.length > 0 ? o.map(i => i.value) : null,
            })
          )
        }
      />
      <OutlinedSelect
        options={STATUS_OPTIONS}
        placeholder="All Statuses"
        isMulti
        value={statusValues as any}
        onChange={o =>
          dispatch(
            setFilter({
              tab: reduxTabKey,
              key: 'status',
              value: Array.isArray(o) && o.length > 0 ? o.map(i => i.value) : null,
            })
          )
        }
      />
      <ResetButton onClick={handleReset}>
        <Icon>close</Icon>
      </ResetButton>
    </Wrapper>
  );
};

export default BuyerTransactionsFilter;
